import {
  Button,
  Checkbox,
  Combobox,
  Tag,
  Text,
  useToastContext,
} from "@ttc3k/trekker";
import { Divider, Flex } from "styled-system/jsx";
import { useMemo, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { differenceInCalendarDays, format } from "date-fns";
import { formatRenewalDate } from "shared/utils";
import { createListCollection } from "@ark-ui/react";
import {
  EmptyState,
  SimpleErrorNotification,
  SimpleLoader,
  WhereaboutsModal,
} from "shared/components";
import { useBackOfficeTags } from "./useBackOfficeTags";
import {
  MemberContactState,
  MembershipBySubjectAndOrganizationDocument,
  MembershipContactStatesByOperatorsDocument,
  OperatorByIdQuery,
  PendingRenewalEmailKind,
  PendingRenewalEmailsDocument,
  useMembershipBySubjectAndOrganizationQuery,
  useMembershipContactStatesByOperatorsQuery,
  useMembershipScheduleRenewalEmailForListingsMutation,
  useMembershipMarkRenewedMutation,
  useMembershipSuggestedRenewalDateLazyQuery,
  useOrganizationByIdQuery,
} from "gql/generated";
import { useFeatureFlag } from "shared/contextProviders";
import { FeatureFlags } from "shared/featureFlags";
import { RenewalDateLine } from "./RenewalDateLine";
import { PendingRenewalEmailsLine } from "./PendingRenewalEmailsLine";
import { CancelMembershipButton } from "apps/directory/Operator/views/BackOffice/CancelMembershipCard";

type BackOfficeTagsProps = {
  operator: OperatorByIdQuery["operatorById"] | undefined;
  operatorEntityId: string | undefined;
  organizationId: string | undefined;
  compact?: boolean;
  readOnly?: boolean;
  onOperatorUpdated?: (updatedOperator: Partial<OperatorByIdQuery["operatorById"]>) => void;
};

export const BackOfficeTags = ({
  organizationId,
  operator,
  operatorEntityId,
  compact = false,
  readOnly = false,
  onOperatorUpdated,
}: BackOfficeTagsProps) => {
  const { t } = useTranslation();
  const { toastFactory } = useToastContext();
  const navigate = useNavigate();
  const {
    errorTags,
    filterableBackOfficeTagsCollection,
    filterableBackOfficeTags,
    loadingTags,
    onTagInputValueSearch,
    onUpdateSelectedTagIds,
    onComboboxOpenChange,
    selectedTagIdsForCurrentOrg,
    membershipTierTagIds,
    membershipTierOptions,
    membershipStatusOptions,
    membershipStatusTagIds,
    hasMembershipConfig,
    membershipLabel,
    listingAudienceTagIds,
    audienceTagOptions,
    audiencesEnabled,
  } = useBackOfficeTags(
    operator,
    operatorEntityId,
    organizationId,
    onOperatorUpdated,
  );

  const [
    pendingMembershipTagRemoval,
    setPendingMembershipTagRemoval,
  ] = useState<{
    nextTagIds: string[];
    tagLabel: string;
    /** True when removing the Pending status tag while an Active tag
     * remains — chains into the mark-renewed offer below on confirm. */
    offerMarkRenewed?: boolean;
  } | null>(null);

  const [pendingEmailSchedule, setPendingEmailSchedule] = useState<{
    emailKind: PendingRenewalEmailKind;
    tagLabel: string;
  } | null>(null);

  const [pendingPaidUpOverride, setPendingPaidUpOverride] = useState<{
    nextStatusIds: string[];
    tagLabel: string;
    paidThroughFormatted: string;
  } | null>(null);

  // Offers explicit "mark renewed" (paid-up date set) alongside a status tag
  // change — triggered when an admin adds the Active tag, or removes Pending
  // while Active remains. `nextTagIds` is the full org-tag-id list to apply.
  const [pendingRenewalTagSync, setPendingRenewalTagSync] = useState<{
    nextTagIds: string[];
    tagLabel: string;
    actionKind: "ADD_ACTIVE" | "REMOVE_PENDING";
    suggestedDate: Date | null;
    blockedByInvoice?: boolean;
    suggestionLoaded: boolean;
  } | null>(null);
  const [alsoMarkRenewed, setAlsoMarkRenewed] = useState(true);
  const renewalOfferRequestRef = useRef(0);

  const renewalEnabled = useFeatureFlag(FeatureFlags.MembershipRenewal);

  const { data: orgData } = useOrganizationByIdQuery({
    variables: { id: organizationId ?? "" },
    skip: !organizationId || !renewalEnabled,
  });
  const renewalSettings = orgData?.organizationById?.metadata?.renewalSettings;

  const { data: membershipData, refetch: refetchMembership } =
    useMembershipBySubjectAndOrganizationQuery({
      variables: {
        subjectType: "OPERATOR",
        subjectEntityId: operatorEntityId ?? "",
        organizationId: organizationId ?? "",
      },
      skip: !operatorEntityId || !organizationId || !renewalEnabled,
    });

  const membership = membershipData?.membershipBySubjectAndOrganization;

  const membershipRefetchQueries =
    operatorEntityId && organizationId
      ? [
          {
            query: MembershipBySubjectAndOrganizationDocument,
            variables: {
              subjectType: "OPERATOR",
              subjectEntityId: operatorEntityId,
              organizationId,
            },
          },
        ]
      : [];

  const [fetchSuggestedRenewalDate] =
    useMembershipSuggestedRenewalDateLazyQuery();
  const [markRenewed, { loading: markingRenewed }] =
    useMembershipMarkRenewedMutation();

  const { data: contactData } = useMembershipContactStatesByOperatorsQuery({
    variables: {
      organizationId: organizationId ?? "",
      operatorEntityIds: operatorEntityId ? [operatorEntityId] : [],
    },
    skip: !operatorEntityId || !organizationId || !renewalEnabled,
    fetchPolicy: "cache-and-network",
  });
  const contactInfo = contactData?.membershipPaidUpByOperators?.find(
    info => info?.operatorEntityId === operatorEntityId,
  );
  const contactKind = contactInfo?.contactKind ?? null;
  const contactState = contactInfo?.contactState ?? null;
  const lastContactedAt = contactInfo?.lastContactedAt
    ? new Date(contactInfo.lastContactedAt)
    : null;
  const daysSinceContact = lastContactedAt
    ? differenceInCalendarDays(new Date(), lastContactedAt)
    : null;
  const [confirmCooldownSend, setConfirmCooldownSend] = useState(false);

  const [scheduleRenewalEmail, { loading: schedulingEmail }] =
    useMembershipScheduleRenewalEmailForListingsMutation();

  const contactRefetchQueries = [
    PendingRenewalEmailsDocument,
    MembershipContactStatesByOperatorsDocument,
  ];

  const handleSendRenewal = async (force = false) => {
    if (!operatorEntityId || !organizationId || !contactKind || schedulingEmail)
      return;
    if (!force && contactState === MemberContactState.CoolingDown) {
      setConfirmCooldownSend(true);
      return;
    }
    try {
      const { data } = await scheduleRenewalEmail({
        variables: {
          organizationId,
          operatorEntityIds: [operatorEntityId],
          emailKind: contactKind,
          force,
        },
        refetchQueries: contactRefetchQueries,
        awaitRefetchQueries: true,
      });
      setConfirmCooldownSend(false);
      const queued =
        (data?.membershipScheduleRenewalEmailForListings?.length ?? 0) > 0;
      toastFactory.create({
        title: queued ? t("core:SUCCESS") : t("core:ERROR.TITLE"),
        description: t(
          queued
            ? "directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.SEND_SUCCESS"
            : "directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.SEND_SKIPPED",
        ),
      });
    } catch {
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t(
          "directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.SEND_ERROR",
        ),
      });
    }
  };

  const emailKindLabel = (kind: PendingRenewalEmailKind): string => {
    switch (kind) {
      case PendingRenewalEmailKind.Reminder:
        return t("directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.KIND_REMINDER");
      case PendingRenewalEmailKind.Expired:
        return t("directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.KIND_EXPIRED");
      case PendingRenewalEmailKind.Overdue:
        return t("directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.KIND_OVERDUE");
      case PendingRenewalEmailKind.Cancelled:
        return t("directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.KIND_CANCELLED");
      case PendingRenewalEmailKind.Custom:
        return t("directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.KIND_CUSTOM");
      default:
        return t("directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.KIND_SUCCESS");
    }
  };

  const contactStatusText = (kind: PendingRenewalEmailKind): string => {
    const prefix = "directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL";
    if (contactState === MemberContactState.NoEmail) {
      return t(`${prefix}.NO_EMAIL`);
    }
    if (contactState === MemberContactState.Unsubscribed) {
      return t(`${prefix}.UNSUBSCRIBED`);
    }
    if (contactState === MemberContactState.Queued) {
      return t(`${prefix}.QUEUED`);
    }
    if (contactState === MemberContactState.Declined) {
      return t(`${prefix}.DECLINED`, { kind: emailKindLabel(kind) });
    }
    if (contactState === MemberContactState.Contacted && lastContactedAt) {
      return t(`${prefix}.LAST_SENT`, {
        date: format(lastContactedAt, "MMM d, yyyy 'at' h:mm a"),
      });
    }
    if (lastContactedAt) {
      return t(`${prefix}.NOT_CONTACTED_LAST_SENT`, {
        kind: emailKindLabel(kind),
        date: format(lastContactedAt, "MMM d, yyyy"),
      });
    }
    return t(`${prefix}.NOT_CONTACTED`, { kind: emailKindLabel(kind) });
  };

  // Map a status tag to the renewal email it triggers, if any is configured.
  const statusTagIdToEmailKind = (
    statusTagId: string,
  ): PendingRenewalEmailKind | null => {
    if (!renewalSettings) return null;
    if (statusTagId === renewalSettings.pendingStatusTagId) {
      return PendingRenewalEmailKind.Reminder;
    }
    if (statusTagId === renewalSettings.expiredStatusTagId) {
      return PendingRenewalEmailKind.Expired;
    }
    if (statusTagId === renewalSettings.activeStatusTagId) {
      return PendingRenewalEmailKind.Success;
    }
    if (
      renewalSettings.sendOverdueEmails &&
      statusTagId === renewalSettings.overdueStatusTagId
    ) {
      return PendingRenewalEmailKind.Overdue;
    }
    if (
      renewalSettings.sendCancelledEmails &&
      statusTagId === renewalSettings.cancelledStatusTagId
    ) {
      return PendingRenewalEmailKind.Cancelled;
    }
    return null;
  };

  const selectedTierIds = useMemo(
    () =>
      selectedTagIdsForCurrentOrg.filter(id =>
        membershipTierTagIds.includes(id),
      ),
    [selectedTagIdsForCurrentOrg, membershipTierTagIds],
  );

  const selectedStatusIds = useMemo(
    () =>
      selectedTagIdsForCurrentOrg.filter(id =>
        membershipStatusTagIds.includes(id),
      ),
    [selectedTagIdsForCurrentOrg, membershipStatusTagIds],
  );

  const customTagIds = useMemo(
    () =>
      hasMembershipConfig || listingAudienceTagIds.length > 0
        ? selectedTagIdsForCurrentOrg.filter(
            id =>
              !membershipTierTagIds.includes(id) &&
              !membershipStatusTagIds.includes(id) &&
              !listingAudienceTagIds.includes(id),
          )
        : selectedTagIdsForCurrentOrg,
    [
      selectedTagIdsForCurrentOrg,
      membershipTierTagIds,
      membershipStatusTagIds,
      listingAudienceTagIds,
      hasMembershipConfig,
    ],
  );

  const selectedAudienceIds = useMemo(
    () =>
      selectedTagIdsForCurrentOrg.filter(id =>
        listingAudienceTagIds.includes(id),
      ),
    [selectedTagIdsForCurrentOrg, listingAudienceTagIds],
  );

  const handleCustomTagsChange = (nextCustomTagIds: string[]) => {
    const nextAllTagIds = [
      ...nextCustomTagIds,
      ...selectedAudienceIds,
      ...selectedTierIds,
      ...selectedStatusIds,
    ];
    onUpdateSelectedTagIds(nextAllTagIds);
  };

  const handleAudienceChange = (nextAudienceIds: string[]) => {
    const withoutAudiences = selectedTagIdsForCurrentOrg.filter(
      id => !listingAudienceTagIds.includes(id),
    );
    onUpdateSelectedTagIds([...withoutAudiences, ...nextAudienceIds]);
  };

  const handleTierChange = (nextTierIds: string[]) => {
    const removedTierId = selectedTierIds.find(
      id => !nextTierIds.includes(id),
    );

    if (removedTierId) {
      const tagLabel =
        membershipTierOptions.find(t => t.value === removedTierId)?.label ??
        membershipLabel;
      const nextAllTagIds = selectedTagIdsForCurrentOrg.filter(
        id => id !== removedTierId,
      );
      setPendingMembershipTagRemoval({ nextTagIds: nextAllTagIds, tagLabel });
      return;
    }

    const withoutTiers = selectedTagIdsForCurrentOrg.filter(
      id => !membershipTierTagIds.includes(id),
    );
    onUpdateSelectedTagIds([...withoutTiers, ...nextTierIds]);
  };

  // Server-derived via getRenewalState() rather than a naive ">30 days out"
  // heuristic on the raw date — PAID_UP is the actual phase boundary the
  // org's policy computes (respecting fixed/anniversary cycles and the T-14
  // reminder window), so this guard agrees with what RenewalDateLine shows
  // instead of drifting from it.
  const isPaidUpBeyondWindow = useCallback((): {
    paidUp: boolean;
    paidThroughFormatted: string;
  } => {
    const raw =
      membership?.renewalState?.paidThroughDate ??
      membership?.manualRenewalDate ??
      membership?.currentPeriodEnd;
    if (!raw) return { paidUp: false, paidThroughFormatted: "" };
    const dateObj = new Date(raw);
    if (isNaN(dateObj.getTime()))
      return { paidUp: false, paidThroughFormatted: "" };
    return {
      paidUp: membership?.renewalState?.phase === "PAID_UP",
      // Calendar-day-only value — see `formatRenewalDate`'s doc comment.
      paidThroughFormatted: formatRenewalDate(dateObj, "MMM d, yyyy"),
    };
  }, [
    membership?.renewalState?.paidThroughDate,
    membership?.renewalState?.phase,
    membership?.manualRenewalDate,
    membership?.currentPeriodEnd,
  ]);

  const isGuardedStatusTag = useCallback(
    (tagId: string): boolean => {
      if (!renewalSettings) return false;
      return (
        tagId === renewalSettings.pendingStatusTagId ||
        tagId === renewalSettings.expiredStatusTagId
      );
    },
    [renewalSettings],
  );

  const applyStatusChange = (nextStatusIds: string[]) => {
    const withoutStatus = selectedTagIdsForCurrentOrg.filter(
      id => !membershipStatusTagIds.includes(id),
    );
    onUpdateSelectedTagIds([...withoutStatus, ...nextStatusIds]);

    const addedStatusId = nextStatusIds.find(
      id => !selectedStatusIds.includes(id),
    );
    if (addedStatusId && operatorEntityId && organizationId) {
      const emailKind = statusTagIdToEmailKind(addedStatusId);
      if (emailKind) {
        const tagLabel =
          membershipStatusOptions.find(o => o.value === addedStatusId)?.label ??
          "";
        setPendingEmailSchedule({ emailKind, tagLabel });
      }
    }
  };

  // Fetches the org-policy-suggested paid-up date and opens the combined
  // "tag change + optional mark renewed" offer dialog. `nextTagIds` is the
  // full org-tag-id list the caller wants applied once confirmed.
  const openMarkRenewedOffer = (
    nextTagIds: string[],
    actionKind: "ADD_ACTIVE" | "REMOVE_PENDING",
    tagLabel: string,
  ) => {
    const requestId = ++renewalOfferRequestRef.current;
    // Default unchecked here (unlike the RenewalDateLine "Mark Renewed"
    // button flow): the primary action here is a tag edit, so touching the
    // paid-up date is an opt-in extra rather than the expected outcome.
    setAlsoMarkRenewed(false);
    setPendingRenewalTagSync({
      nextTagIds,
      tagLabel,
      actionKind,
      suggestedDate: null,
      suggestionLoaded: false,
    });
    if (!operatorEntityId || !organizationId) {
      setPendingRenewalTagSync(prev =>
        prev ? { ...prev, suggestionLoaded: true } : prev,
      );
      return;
    }
    void fetchSuggestedRenewalDate({
      variables: {
        subjectType: "OPERATOR",
        subjectEntityId: operatorEntityId,
        organizationId,
      },
      fetchPolicy: "network-only",
    }).then(({ data }) => {
      if (renewalOfferRequestRef.current !== requestId) return;
      const suggestion = data?.membershipSuggestedRenewalDate;
      const blockedByInvoice = !!suggestion?.blockedByInvoice;
      const suggestedDate = blockedByInvoice ? null : suggestion?.suggestedDate;
      setPendingRenewalTagSync(prev =>
        prev
          ? {
              ...prev,
              suggestedDate: suggestedDate ? new Date(suggestedDate) : null,
              blockedByInvoice,
              suggestionLoaded: true,
            }
          : prev,
      );
    });
  };

  const handleStatusChange = (nextStatusIds: string[]) => {
    const removedStatusId = selectedStatusIds.find(
      id => !nextStatusIds.includes(id),
    );

    if (removedStatusId) {
      const tagLabel =
        membershipStatusOptions.find(t => t.value === removedStatusId)?.label ??
        membershipLabel;
      const nextAllTagIds = selectedTagIdsForCurrentOrg.filter(
        id => id !== removedStatusId,
      );
      const offerMarkRenewed =
        !!renewalSettings?.pendingStatusTagId &&
        removedStatusId === String(renewalSettings.pendingStatusTagId) &&
        !!renewalSettings?.activeStatusTagId &&
        nextAllTagIds.includes(String(renewalSettings.activeStatusTagId));
      setPendingMembershipTagRemoval({
        nextTagIds: nextAllTagIds,
        tagLabel,
        offerMarkRenewed,
      });
      return;
    }

    const addedStatusId = nextStatusIds.find(
      id => !selectedStatusIds.includes(id),
    );

    if (addedStatusId && isGuardedStatusTag(addedStatusId)) {
      const { paidUp, paidThroughFormatted } = isPaidUpBeyondWindow();
      if (paidUp) {
        const tagLabel =
          membershipStatusOptions.find(o => o.value === addedStatusId)?.label ??
          "";
        setPendingPaidUpOverride({
          nextStatusIds,
          tagLabel,
          paidThroughFormatted,
        });
        return;
      }
    }

    if (
      addedStatusId &&
      !!renewalSettings?.activeStatusTagId &&
      addedStatusId === String(renewalSettings.activeStatusTagId)
    ) {
      const withoutStatus = selectedTagIdsForCurrentOrg.filter(
        id => !membershipStatusTagIds.includes(id),
      );
      const nextAllTagIds = [...withoutStatus, ...nextStatusIds];
      const tagLabel =
        membershipStatusOptions.find(o => o.value === addedStatusId)?.label ??
        membershipLabel;
      openMarkRenewedOffer(nextAllTagIds, "ADD_ACTIVE", tagLabel);
      return;
    }

    applyStatusChange(nextStatusIds);
  };

  const audienceCollection = useMemo(
    () => createListCollection({ items: audienceTagOptions }),
    [audienceTagOptions],
  );

  const tierCollection = useMemo(
    () => createListCollection({ items: membershipTierOptions }),
    [membershipTierOptions],
  );

  const statusCollection = useMemo(
    () => createListCollection({ items: membershipStatusOptions }),
    [membershipStatusOptions],
  );

  const renderTags = (tagIds: string[], tagOptions: typeof filterableBackOfficeTags) =>
    tagIds.length > 0 ? (
      <Flex gap="100" flexWrap="wrap">
        {tagIds.map(tagId => {
          const tagOption = tagOptions.find(t => t.value === tagId);
          return tagOption ? (
            <Tag key={tagId} size="sm" visual="primary" mode="light">
              {tagOption.label}
            </Tag>
          ) : null;
        })}
      </Flex>
    ) : !loadingTags ? (
      <Flex>
        <Tag size="sm" visual="neutral" mode="light">
          {t("directory:OPERATOR.BACK_OFFICE.TAGS.NONE")}
        </Tag>
      </Flex>
    ) : (
      <SimpleLoader />
    );

  const customTagsContent = (
    <>
      {readOnly ? (
        renderTags(customTagIds, filterableBackOfficeTags)
      ) : filterableBackOfficeTags.length > 0 && !loadingTags ? (
        <Combobox.Root
          value={customTagIds}
          onValueChange={d => handleCustomTagsChange(d.value)}
          onInputValueChange={d => onTagInputValueSearch(d.inputValue)}
          onOpenChange={d => onComboboxOpenChange(d.open)}
          collection={filterableBackOfficeTagsCollection}
          placeholder={`${t("directory:OPERATOR.BACK_OFFICE.TAGS.PLACEHOLDER_LABEL")}...`}
          label={t("directory:OPERATOR.BACK_OFFICE.TAGS.TITLE")}
          multiple
        >
          {filterableBackOfficeTagsCollection.items.map(item => (
            <Combobox.Item key={item.value} item={item} {...item} />
          ))}
        </Combobox.Root>
      ) : (
        <>
          {errorTags ? (
            <SimpleErrorNotification />
          ) : (
            <>
              {filterableBackOfficeTags.length < 1 && !loadingTags ? (
                <EmptyState
                  text={t("directory:OPERATOR.BACK_OFFICE.TAGS.EMPTY")}
                  ctaButtonProps={{
                    children: t(
                      "directory:OPERATOR.BACK_OFFICE.TAGS.EMPTY_CTA",
                    ),
                    size: "md",
                    onClick: () =>
                      navigate(
                        `/app/organization/${organizationId}/home?view=SETTINGS&settingsSubView=CUSTOM_TAGS`,
                      ),
                  }}
                />
              ) : (
                <SimpleLoader />
              )}
            </>
          )}
        </>
      )}
    </>
  );

  const audienceContent =
    audiencesEnabled && audienceTagOptions.length > 0 && !readOnly ? (
      <Combobox.Root
        value={selectedAudienceIds}
        onValueChange={d => handleAudienceChange(d.value)}
        collection={audienceCollection}
        placeholder={t(
          "directory:OPERATOR.BACK_OFFICE.AUDIENCE_TAGS.PLACEHOLDER",
        )}
        label={t("directory:OPERATOR.BACK_OFFICE.AUDIENCE_TAGS.LABEL")}
        multiple
      >
        {audienceCollection.items.map(item => (
          <Combobox.Item key={item.value} item={item} {...item} />
        ))}
      </Combobox.Root>
    ) : audiencesEnabled && audienceTagOptions.length > 0 && readOnly ? (
      renderTags(selectedAudienceIds, audienceTagOptions)
    ) : null;

  const membershipContent = hasMembershipConfig && !readOnly ? (
    <Flex flexDir="column" gap="300">
      {membershipTierOptions.length > 0 && (
        <Combobox.Root
          value={selectedTierIds}
          onValueChange={d => handleTierChange(d.value)}
          collection={tierCollection}
          placeholder={t(
            "directory:OPERATOR.BACK_OFFICE.MEMBERSHIP_TAGS.TIER_PLACEHOLDER",
          )}
          label={t("directory:OPERATOR.BACK_OFFICE.MEMBERSHIP_TAGS.TIER_LABEL", { membership: membershipLabel })}
          multiple
        >
          {tierCollection.items.map(item => (
            <Combobox.Item key={item.value} item={item} {...item} />
          ))}
        </Combobox.Root>
      )}

      {membershipStatusOptions.length > 0 && (
        <Combobox.Root
          value={selectedStatusIds}
          onValueChange={d => handleStatusChange(d.value)}
          collection={statusCollection}
          placeholder={t(
            "directory:OPERATOR.BACK_OFFICE.MEMBERSHIP_TAGS.STATUS_PLACEHOLDER",
          )}
          label={t(
            "directory:OPERATOR.BACK_OFFICE.MEMBERSHIP_TAGS.STATUS_LABEL",
            { membership: membershipLabel },
          )}
          multiple
        >
          {statusCollection.items.map(item => (
            <Combobox.Item key={item.value} item={item} {...item} />
          ))}
        </Combobox.Root>
      )}
    </Flex>
  ) : null;

  const membershipTagRemovalDialog = pendingMembershipTagRemoval ? (
    <WhereaboutsModal
      isOpen={!!pendingMembershipTagRemoval}
      onClose={() => setPendingMembershipTagRemoval(null)}
      title={t("directory:OPERATOR.BACK_OFFICE.MEMBERSHIP_TAGS.REMOVE_CONFIRM_TITLE", {
        membership: membershipLabel,
      })}
      size="sm"
      isMaxZIndex
      saveButtonCopy={t(
        "directory:OPERATOR.BACK_OFFICE.MEMBERSHIP_TAGS.REMOVE_CONFIRM",
      )}
      saveButtonProps={{
        visual: "destructive",
        onClick: () => {
          const removal = pendingMembershipTagRemoval;
          setPendingMembershipTagRemoval(null);
          if (!removal) return;
          if (removal.offerMarkRenewed) {
            openMarkRenewedOffer(
              removal.nextTagIds,
              "REMOVE_PENDING",
              removal.tagLabel,
            );
          } else {
            onUpdateSelectedTagIds(removal.nextTagIds);
          }
        },
      }}
      cancelButtonProps={{
        onClick: () => setPendingMembershipTagRemoval(null),
      }}
    >
      <Text color="text.mid">
        {t(
          "directory:OPERATOR.BACK_OFFICE.MEMBERSHIP_TAGS.REMOVE_CONFIRM_MESSAGE",
          {
            membership: membershipLabel,
            tagName: pendingMembershipTagRemoval.tagLabel,
          },
        )}
      </Text>
    </WhereaboutsModal>
  ) : null;

  const showConfiguredTagsDivider =
    customTagsContent && (audienceContent || membershipContent);

  const wrappedContent = (
    <Flex
      flexDir="column"
      gap="300"
      p="300"
      borderWidth="1px"
      borderStyle="dashed"
      borderColor="border.light"
      borderRadius="150"
    >
      {customTagsContent}
      {showConfiguredTagsDivider ? <Divider /> : null}
      {audienceContent}
      {membershipContent}
      {renewalEnabled && hasMembershipConfig && membership?.tierTagId && (
        <RenewalDateLine
          membership={membership}
          renewalSettings={renewalSettings}
          operatorEntityId={operatorEntityId}
          organizationId={organizationId}
          readOnly={readOnly}
          currentStatusTagIds={selectedStatusIds}
          onApplyStatusTagSync={applyStatusChange}
          onMembershipUpdated={refetchMembership}
        />
      )}
      {renewalEnabled && !readOnly && operatorEntityId && organizationId && (
        <PendingRenewalEmailsLine
          organizationId={organizationId}
          operatorEntityId={operatorEntityId}
        />
      )}
      {renewalEnabled &&
        hasMembershipConfig &&
        contactKind &&
        !readOnly && (
          <Flex flexDir="column" gap="100">
            <Button
              type="button"
              visual="accent.blue"
              disabled={
                schedulingEmail ||
                contactState === MemberContactState.Queued ||
                contactState === MemberContactState.NoEmail ||
                contactState === MemberContactState.Unsubscribed
              }
              isLoading={schedulingEmail}
              onClick={() => void handleSendRenewal()}
            >
              {t("directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.SEND_KIND_BUTTON", {
                kind: emailKindLabel(contactKind),
              })}
            </Button>
            <Text visual="smallRegular" color="text.mid">
              {contactStatusText(contactKind)}
            </Text>
          </Flex>
        )}
      {compact &&
        renewalEnabled &&
        hasMembershipConfig &&
        selectedTierIds.length > 0 &&
        !readOnly && (
          <CancelMembershipButton membershipLabel={membershipLabel} />
        )}
    </Flex>
  );

  const scheduleEmailDialog = pendingEmailSchedule ? (
    <WhereaboutsModal
      isOpen={!!pendingEmailSchedule}
      onClose={() => setPendingEmailSchedule(null)}
      title={t("directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.SCHEDULE_TITLE")}
      size="sm"
      isMaxZIndex
      saveButtonCopy={t(
        "directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.SCHEDULE_CONFIRM",
      )}
      saveButtonProps={{
        visual: "accent.blue",
        isLoading: schedulingEmail,
        onClick: async () => {
          if (!operatorEntityId || !organizationId) {
            setPendingEmailSchedule(null);
            return;
          }
          // Confirming right after a status change is an explicit admin
          // choice, so it bypasses the cooldown (the dialog shows the last send).
          await scheduleRenewalEmail({
            variables: {
              organizationId,
              operatorEntityIds: [operatorEntityId],
              emailKind: pendingEmailSchedule.emailKind,
              force: true,
            },
            refetchQueries: contactRefetchQueries,
          });
          setPendingEmailSchedule(null);
        },
      }}
      cancelButtonCopy={t(
        "directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.SCHEDULE_DISMISS",
      )}
      cancelButtonProps={{
        onClick: () => setPendingEmailSchedule(null),
      }}
    >
      <Flex flexDir="column" gap="150">
        <Text color="text.mid">
          {t("directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.SCHEDULE_MESSAGE", {
            kind: emailKindLabel(pendingEmailSchedule.emailKind),
            tagName: pendingEmailSchedule.tagLabel,
          })}
        </Text>
        {contactState === MemberContactState.CoolingDown &&
          daysSinceContact !== null && (
            <Text visual="smallRegular" color="text.mid">
              {t("directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.RECENTLY_SENT", {
                count: daysSinceContact,
              })}
            </Text>
          )}
      </Flex>
    </WhereaboutsModal>
  ) : null;

  const cooldownSendDialog =
    confirmCooldownSend && contactKind ? (
      <WhereaboutsModal
        isOpen={confirmCooldownSend}
        onClose={() => setConfirmCooldownSend(false)}
        title={t("directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.COOLDOWN_TITLE")}
        size="sm"
        isMaxZIndex
        saveButtonCopy={t(
          "directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.COOLDOWN_CONFIRM",
        )}
        saveButtonProps={{
          visual: "accent.blue",
          isLoading: schedulingEmail,
          onClick: () => void handleSendRenewal(true),
        }}
        cancelButtonProps={{ onClick: () => setConfirmCooldownSend(false) }}
      >
        <Text color="text.mid">
          {t("directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.COOLDOWN_MESSAGE", {
            count: daysSinceContact ?? 0,
            kind: emailKindLabel(contactKind),
          })}
        </Text>
      </WhereaboutsModal>
    ) : null;

  const paidUpOverrideDialog = pendingPaidUpOverride ? (
    <WhereaboutsModal
      isOpen={!!pendingPaidUpOverride}
      onClose={() => setPendingPaidUpOverride(null)}
      title={t(
        "directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.PAID_UP_WARN_TITLE",
      )}
      size="sm"
      isMaxZIndex
      saveButtonCopy={t(
        "directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.PAID_UP_WARN_CONFIRM",
      )}
      saveButtonProps={{
        visual: "destructive",
        onClick: () => {
          applyStatusChange(pendingPaidUpOverride.nextStatusIds);
          setPendingPaidUpOverride(null);
        },
      }}
      cancelButtonCopy={t(
        "directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.PAID_UP_WARN_CANCEL",
      )}
      cancelButtonProps={{
        onClick: () => setPendingPaidUpOverride(null),
      }}
    >
      <Text color="text.mid">
        {t(
          "directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.PAID_UP_WARN_MESSAGE",
          {
            date: pendingPaidUpOverride.paidThroughFormatted,
            tagName: pendingPaidUpOverride.tagLabel,
          },
        )}
      </Text>
    </WhereaboutsModal>
  ) : null;

  const renewalTagSyncDialog = pendingRenewalTagSync ? (
    <WhereaboutsModal
      isOpen
      onClose={() => setPendingRenewalTagSync(null)}
      title={
        pendingRenewalTagSync.actionKind === "ADD_ACTIVE"
          ? t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.SYNC_ADD_ACTIVE_TITLE")
          : t(
              "directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.SYNC_REMOVE_PENDING_TITLE",
            )
      }
      size="sm"
      isMaxZIndex
      saveButtonCopy={t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.SYNC_CONFIRM")}
      saveButtonProps={{
        disabled: markingRenewed,
        isLoading: markingRenewed,
        onClick: async () => {
          const sync = pendingRenewalTagSync;
          if (!sync) return;
          setPendingRenewalTagSync(null);
          await onUpdateSelectedTagIds(sync.nextTagIds);
          if (alsoMarkRenewed && sync.suggestedDate && operatorEntityId && organizationId) {
            await markRenewed({
              variables: { operatorEntityId, organizationId },
              refetchQueries: membershipRefetchQueries,
              awaitRefetchQueries: true,
            });
            await refetchMembership();
          }
        },
      }}
      cancelButtonProps={{ onClick: () => setPendingRenewalTagSync(null) }}
    >
      <Flex flexDir="column" gap="200">
        <Text color="text.mid">
          {pendingRenewalTagSync.actionKind === "ADD_ACTIVE"
            ? t(
                "directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.SYNC_ADD_ACTIVE_MESSAGE",
                { tagName: pendingRenewalTagSync.tagLabel },
              )
            : t(
                "directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.SYNC_REMOVE_PENDING_MESSAGE",
                { tagName: pendingRenewalTagSync.tagLabel },
              )}
        </Text>
        {!pendingRenewalTagSync.suggestionLoaded ? (
          <SimpleLoader />
        ) : pendingRenewalTagSync.suggestedDate ? (
          <Checkbox
            label={t(
              "directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.SYNC_ALSO_MARK_RENEWED",
              { date: formatRenewalDate(pendingRenewalTagSync.suggestedDate, "MMMM d, yyyy") },
            )}
            checked={alsoMarkRenewed}
            onCheckedChange={({ checked }) => setAlsoMarkRenewed(checked === true)}
          />
        ) : (
          <Text visual="smallRegular" color="text.mid">
            {pendingRenewalTagSync.blockedByInvoice
              ? t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.SYNC_BLOCKED_BY_INVOICE")
              : t("directory:OPERATOR.BACK_OFFICE.RENEWAL_DATE.SYNC_NO_SUGGESTION")}
          </Text>
        )}
      </Flex>
    </WhereaboutsModal>
  ) : null;

  if (compact) {
    return (
      <>
        {wrappedContent}
        {membershipTagRemovalDialog}
        {scheduleEmailDialog}
        {cooldownSendDialog}
        {renewalTagSyncDialog}
        {paidUpOverrideDialog}
      </>
    );
  }

  return (
    <>
      {wrappedContent}
      {membershipTagRemovalDialog}
      {scheduleEmailDialog}
      {cooldownSendDialog}
      {renewalTagSyncDialog}
      {paidUpOverrideDialog}
    </>
  );
};
